export interface Patient {
    id: string;
    first_name: string;
    last_name: string;
    contact_phone: string;
    contact_email: string;
    created_at?: string;
    therapist_id?: string;
    /** The centre holding this record, or null for the therapist's own patient. */
    center_id?: string | null;
    consent_ai_analysis?: boolean;
    consent_date?: string; // ISO string

    // Expanded fields (Issue #14)
    pronouns?: string;
    date_of_birth?: string; // ISO string
    sex?: string;
    street?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country: string;
    /** IANA identifier. Absent means the patient's zone is not known, and
     *  their session times are written in the therapist's. */
    timezone?: string | null;
    /** A training centre's student: the course they are in and the year of it. */
    course_id?: string | null;
    academic_year?: number | null;
    /** Where WhatsApp messages go, in E.164 form. Can differ from `contact_phone`. */
    whatsapp_phone?: string | null;
    /**
     * Whether the patient has opted in to automatic WhatsApp messages. Their
     * own switch, in their portal: read-only here, and off until they turn it on.
     */
    whatsapp_opt_in?: boolean;
}

/** Whether the patient has a WhatsApp number but hasn't opted in to automatic WhatsApp messages. */
export function refusesWhatsApp(patient: Pick<Patient, "whatsapp_phone" | "whatsapp_opt_in">): boolean {
    return Boolean(patient.whatsapp_phone) && patient.whatsapp_opt_in === false;
}

export interface CreatePatientDTO {
    first_name: string;
    last_name: string;
    contact_phone: string;
    contact_email: string;
    therapist_id: string;
    /**
     * For a therapist in a centre: the centre's patient (the centre sees them)
     * or their own. Left out, a centre member's new patient is the centre's.
     */
    held_by_center?: boolean;
    pronouns?: string;
    date_of_birth?: string; // ISO string
    sex?: string;
    street?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    timezone?: string | null;
    /** A training centre's student: the course they are in and the year of it. */
    course_id?: string | null;
    academic_year?: number | null;
    /** E.164, or null to clear it. */
    whatsapp_phone?: string | null;
    /**
     * The centre's own fields, by field id, for a patient the centre will
     * hold. Sent, it is checked whole — required fields included — before the
     * patient is created.
     */
    custom_values?: Record<string, string | null>;
}

/** Custom values change through their own endpoint, never with the record. */
export type UpdatePatientDTO = Partial<Omit<CreatePatientDTO, "custom_values">>;

// --- Domain utility functions ---------------------------------

export function getPatientFullName(patient: Patient): string {
    return `${patient.first_name} ${patient.last_name}`;
}

export function getPatientAge(patient: Patient): number | null {
    if (!patient.date_of_birth) return null;
    const dob = new Date(patient.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age;
}

export function hasPatientGivenConsent(patient: Patient): boolean {
    return Boolean(patient.consent_ai_analysis && patient.consent_date);
}

export function isPatientProfileComplete(patient: Patient): boolean {
    return Boolean(
        patient.first_name &&
        patient.last_name &&
        patient.contact_email &&
        patient.contact_phone
    );
}
